import React, { useState } from 'react';
import Link from 'next/link';
import {
  Table,
  Tbody,
  Tr,
  Td,
  Text,
  Heading,
  Box,
  Flex,
  Button,
  Icon,
  Thead,
  Th,
  chakra,
  useMediaQuery
} from '@chakra-ui/react';
import { openPopupWidget } from 'react-calendly';
import { URLS } from '@lib';
import { LayoutContainer } from '@components/common';
import { CheckIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid';
import { Tooltip } from '@components/common';
import type { Tiers } from './plans-content';
import { motion, isValidMotionProp, useAnimation } from 'framer-motion';
import { analytics } from 'pages';
import { rudderTrack } from '@hooks/rudderTrack';

const plans = [
  {
    title: 'Free',
    featured: false,
    description:
      'Up to 5,000 monthly active users and unlimited sign-ins. No credit card required.'
  },
  {
    title: 'Hobby',
    featured: false,
    description: 'Essential features for launching or growing any application.'
  },
  {
    title: 'Business',
    featured: true,
    description: 'Everything you need for a growing business.'
  },
  {
    title: 'Enterprise',
    featured: true,
    description: 'Bulk discounts, custom contracts and enterprise support.'
  }
];

type Feature = {
  name: string;
  tooltip?: string;
  comingSoon?: string;
  tiers: Array<{
    title: Tiers;
    value: string | boolean;
  }>;
};

type FeatureSection = {
  name: string;
  features: Feature[];
};

export const featureSections: FeatureSection[] = [
  {
    name: 'Platform pricing',
    features: [
      {
        name: 'Base price',
        tiers: [
          { title: 'Free', value: 'Free' },
          { title: 'Hobby', value: '$25' },
          { title: 'Business', value: '$99' },
          { title: 'Enterprise', value: 'Contact sales' }
        ]
      },
      {
        name: 'Included monthly active users (MAUs) ',
        tooltip: "A user is 'active' if they visit your application",
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: '1,000' },
          { title: 'Business', value: '1,000' },
          { title: 'Enterprise', value: 'Contact sales' }
        ]
      },
      {
        name: 'Additional MAUs',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: '$0.02 each' },
          { title: 'Business', value: '$0.05 each' },
          { title: 'Enterprise', value: 'Volume discounts' }
        ]
      },
      {
        name: 'MAU cap',
        tiers: [
          { title: 'Free', value: '5,000' },
          { title: 'Hobby', value: 'No cap' },
          { title: 'Business', value: 'No cap' },
          { title: 'Enterprise', value: 'No cap' }
        ]
      },
      {
        name: 'Included monthly active orgs (MAOs)',
        tooltip:
          "An organization is 'active' if there are at least 2 members, and at least one of those members is active.",
        tiers: [
          { title: 'Free', value: '10' },
          { title: 'Hobby', value: '100' },
          { title: 'Business', value: '100' },
          { title: 'Enterprise', value: 'Contact sales' }
        ]
      },
      {
        name: 'Additional MAOs',

        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: '$0.25 each' },
          { title: 'Business', value: '$1 each' },
          { title: 'Enterprise', value: 'Volume discounts' }
        ]
      },
      {
        name: 'Allowed Clerk collaborators',
        tiers: [
          { title: 'Free', value: '3' },
          { title: 'Hobby', value: '3' },
          { title: 'Business', value: '3' },
          { title: 'Enterprise', value: '3' }
        ]
      },
      {
        name: 'Additional Clerk collaborators',
        tiers: [
          { title: 'Free', value: '$20 each' },
          { title: 'Hobby', value: '$20 each' },
          { title: 'Business', value: '$20 each' },
          { title: 'Enterprise', value: '$20 each' }
        ]
      }
    ]
  },

  {
    name: 'Messaging pricing',
    features: [
      {
        name: 'Verification emails',
        tiers: [
          { title: 'Free', value: 'Included' },
          { title: 'Hobby', value: 'Included' },
          { title: 'Business', value: 'Included' },
          { title: 'Enterprise', value: 'Included' }
        ]
      },
      {
        name: 'Invitation emails',
        tiers: [
          { title: 'Free', value: 'Included' },
          { title: 'Hobby', value: 'Included' },
          { title: 'Business', value: 'Included' },
          { title: 'Enterprise', value: 'Included' }
        ]
      },
      {
        name: 'Transactional emails',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: '$0.001 each' },
          { title: 'Business', value: '$0.001 each' },
          { title: 'Enterprise', value: '$0.001 each' }
        ]
      },
      {
        name: 'Verification SMSs',
        tooltip:
          'SMS rates depend on the country.  Our SMS vendor is Twilio, and we passthrough their fees.',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: 'Market rate' },
          { title: 'Business', value: 'Market rate' },
          { title: 'Enterprise', value: 'Market rate' }
        ]
      },
      {
        name: 'Transactional SMSs',
        tooltip:
          'SMS rates depend on the country.  Our SMS vendor is Twilio, and we passthrough their fees.',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: 'Market rate' },
          { title: 'Business', value: 'Market rate' },
          { title: 'Enterprise', value: 'Market rate' }
        ]
      }
    ]
  },

  {
    name: 'UIs & Branding',
    features: [
      {
        name: 'Hosted pages',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Pre-built components',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'CSS customization',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Build your own UIs',
        tooltip: "For completely custom UIs, leverage Clerk's powerful APIs.",
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Custom domain',
        tooltip: 'Deploy hosted UIs and send emails from your own domain',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Remove Clerk branding',
        tooltip:
          "Remove the 'Secured by Clerk' branding in Clerk's pre-built components & hosted UIs",
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      }
    ]
  },

  {
    name: 'Authentication features',
    features: [
      {
        name: 'Passwords',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Password complexity requirements',
        tooltip:
          'Add additional requirements for passwords to include numbers and uppercase, lowercase, and special characters.',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Magic links / Email Codes',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'SMS Phone codes',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Social logins / Web3 wallets',
        tiers: [
          { title: 'Free', value: 'Up to 3' },
          { title: 'Hobby', value: 'Unlimited' },
          { title: 'Business', value: 'Unlimited' },
          { title: 'Enterprise', value: 'Unlimited' }
        ]
      },
      {
        name: 'Custom auth methods',
        tooltip: 'Build your own authentication methods.',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Allowlist/Blocklist',
        tooltip: 'Only allow certain people to access your application.',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'SMS Multifactor auth',
        tooltip: 'Allow users to add a phone as a second factor.',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'TOTP Multifactor auth',
        tooltip: 'Allow users to add a TOTP device for enhanced security.',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'SAML',
        comingSoon: `Beta`,
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      }
    ]
  },

  {
    name: 'User management features',
    features: [
      {
        name: 'Admin Dashboard',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Account linking',
        tooltip:
          'Prevent duplicate accounts when the same user signs in with a new strategy. (e.g. social sign-in after signing up by email)',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'User bans',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'User impersonation',
        tooltip: 'Sign in as your users for support and debugging.',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      }
    ]
  },

  {
    name: 'Session management features',
    features: [
      {
        name: 'Revocable sessions',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Custom JWT SSO templates',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Custom session tokens',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Custom session duration',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'User switching',
        tooltip:
          'Allow users to sign into multiple accounts simultaneously and switch between them',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Device management',
        tooltip:
          'Allow users to manage sessions across devices and sign out remotely',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      }
    ]
  },

  {
    name: 'Organizations features',
    features: [
      {
        name: 'Allowed users per org',
        tiers: [
          { title: 'Free', value: '3' },
          { title: 'Hobby', value: '10' },
          { title: 'Business', value: 'Unlimited' },
          { title: 'Enterprise', value: 'Unlimited' }
        ]
      },
      {
        name: 'Invitation emails',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Basic RBAC',
        tooltip: "Users can either be an 'Admin' or a 'Member'.",
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Custom Roles',
        tooltip: 'Define your own roles and permissions.',
        comingSoon: `Q3 2023`,
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: false },
          { title: 'Enterprise', value: false }
        ]
      }
    ]
  },
  {
    name: 'Support & Compliance',
    features: [
      {
        name: 'Exportable user data',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ],
        tooltip: 'Export user data, including the bcrypt hash of user passwords'
      },
      {
        name: 'Community Support',
        tiers: [
          { title: 'Free', value: true },
          { title: 'Hobby', value: true },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'Priority Support',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'SOC2 Report',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: true },
          { title: 'Enterprise', value: true }
        ]
      },
      {
        name: 'HIPAA/BAA',
        tiers: [
          { title: 'Free', value: false },
          { title: 'Hobby', value: false },
          { title: 'Business', value: false },
          { title: 'Enterprise', value: true }
        ]
      }
    ]
  }
];

const ChakraBox = chakra(motion.div, {
  /**
   * Allow motion props and the children prop to be forwarded.
   * All other chakra props not matching the motion props will still be forwarded.
   */
  shouldForwardProp: prop => isValidMotionProp(prop) || prop === 'children'
});

const StickyHeader = (): JSX.Element => {
  // @ts-ignore

  return (
    <Tr
      pos="fixed"
      top={'81px'}
      as="header"
      borderColor="gray.200"
      transition="all 0.2s"
      bg="white"
    >
      {' '}
      <Th>
        <Heading minW="300px" size="h5" textAlign={'center'}></Heading>
      </Th>
      <Th>
        <Heading minW="200px" size="h5" textAlign={'center'}>
          Free
        </Heading>
      </Th>
      <Th>
        <Heading minW="244px" size="h5" textAlign={'center'}>
          Hobby
        </Heading>
      </Th>
      <Th borderLeft={'2px'} borderRight={'2px'} borderColor="primary.500">
        <Heading minW="168" size="h5" textAlign={'center'}>
          Business
        </Heading>
      </Th>
      <Th>
        <Heading minW="220px" ml={'35px'} size="h5" textAlign={'center'}>
          Enterprise
        </Heading>
      </Th>
    </Tr>
  );
};

export function PlansTable(): JSX.Element {
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  const controls = useAnimation();

  const featuresLength = featureSections
    .map(val => val.features)
    .reduce((pre, cur) => pre.concat(cur))
    .map(e => ({ name: e.name, tiers: e.tiers })).length;

  const height = featuresLength * 53 + featureSections.length * 100 + 142;
  const [isSmallerThan1400] = useMediaQuery('(max-width: 1400px)');
  const yOffset = isSmallerThan1400 ? 2231 : 1672;

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const onScroll = () => {
        const scrollY = window.scrollY;
        if (scrollY > yOffset && scrollY <= 4974 && !hasScrolled) {
          setHasScrolled(true);
        } else if (
          (scrollY <= yOffset && hasScrolled) ||
          (scrollY > 4974 && hasScrolled)
        ) {
          setHasScrolled(false);
        }
      };

      window.removeEventListener('scroll', onScroll);
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }
  }, [hasScrolled, yOffset]);

  const sequence = async () => {
    return await controls.start({
      opacity: [0, 1],
      transition: { duration: 0.05 }
    });
  };

  const fadeOut = async () => {
    return await controls.start({
      opacity: [1, 0],
      transition: { duration: 0.05 }
    });
  };

  React.useEffect(() => {
    if (hasScrolled) {
      sequence();
    } else {
      fadeOut();
    }
  });

  return (
    <LayoutContainer
      verticalPadding="none"
      bg="white"
      pos="relative"
      justifySelf={'center'}
      alignSelf={'center'}
    >
      <>
        <Table variant={'unstyled'}>
          <Thead>
            <Tr display="flex" justifyContent="space-between">
              <Th p={0} w="220px">
                {/* <Heading size="h4" py={6} flex={1} w={200}>
                  Explore All of
                  <br />
                  <Box
                    as="span"
                    bgGradient="linear-gradient(270.11deg, #17CCFC 0.1%, #6C47FF 97.84%), #262F40"
                    bgClip="text"
                  >
                    Clerk&apos;s Features
                  </Box>
                </Heading> */}
              </Th>
              {Object.values(plans).map(({ title }) => (
                <Th key={title}>
                  <Flex
                    direction="column"
                    key={title}
                    textAlign="center"
                    pt={5}
                    flex={1}
                    pos="relative"
                    w="220px"
                  >
                    <Box
                      w="100%"
                      borderWidth="2px"
                      py={5}
                      borderRadius="xl"
                      borderColor="primary.500"
                      pos="absolute"
                      top="0%"
                      left="-1%"
                      h={height}
                      display={title === 'Business' ? 'block' : 'none'}
                    >
                      <ChakraBox
                        as={motion.div}
                        animate={controls}
                        display={!hasScrolled ? 'none' : 'block'}
                        pos="relative"
                        right="890px"
                      >
                        <StickyHeader />
                      </ChakraBox>
                    </Box>
                    <Heading size="h5" pb="20px">
                      {title}
                    </Heading>

                    {title === 'Enterprise' ? (
                      <Button
                        border="1px"
                        onClick={() => {
                          openPopupWidget({
                            url: URLS.calendlyUrl
                          });
                          analytics.track(
                            'Marketing_Pricing_Talk_To_An_Expert Button Clicked',
                            {
                              ClickedOn: 'Enterprise',
                              surface: 'Marketing',
                              location: 'Pricing'
                            }
                          );
                          rudderTrack(
                            'Marketing_Pricing_Talk_To_An_Expert Button Clicked',
                            {
                              ClickedOn: 'Enterprise',
                              surface: 'Marketing',
                              location: 'Pricing'
                            }
                          );
                        }}
                        h="48px"
                        alignSelf="center"
                      >
                        Talk with an Expert
                      </Button>
                    ) : (
                      <Link
                        legacyBehavior
                        href={
                          process.env.NEXT_PUBLIC_DASHBOARD_URL + '/sign-up'
                        }
                        passHref
                      >
                        <Button
                          as="a"
                          h="48px"
                          alignSelf="center"
                          variant="white"
                          border={title === 'Business' ? '1px' : '0px'}
                          borderColor={'primary.500'}
                          onClick={() => {
                            analytics.track(
                              'Marketing_Pricing_Start Building Button Clicked',
                              {
                                ClickedOn: title,
                                surface: 'Marketing',
                                location: 'Pricing'
                              }
                            );
                            rudderTrack(
                              'Marketing_Pricing_Start Building Button Clicked',
                              {
                                ClickedOn: title,
                                surface: 'Marketing',
                                location: 'Pricing'
                              }
                            );
                          }}
                        >
                          Start building now
                        </Button>
                      </Link>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>

          {React.Children.toArray(
            featureSections.map((featureSection, index) => (
              <Tbody mt="-40px">
                <Tr>
                  <Text
                    fontWeight={'semibold'}
                    fontSize="lg"
                    pt={'40px'}
                    pb={4}
                  >
                    {featureSection.name}
                  </Text>
                </Tr>
                {React.Children.toArray(
                  featureSection.features.map((feature, featureIndex) => {
                    return (
                      <Tr
                        key={feature.name}
                        display="flex"
                        justifyContent="space-between"
                        ml={-6}
                      >
                        <Td
                          display="flex"
                          justifyContent="flex-start"
                          borderBottom={'1px solid #E4E9F2'}
                          minW="249.5px"
                        >
                          <Flex
                            flexDir="row"
                            alignItems="center"
                            justify="space-between"
                            w="full"
                          >
                            <Text>{feature.name}</Text>
                            {feature.tooltip && !feature.comingSoon ? (
                              <Tooltip
                                popoverClassName="ml-1.5"
                                title={
                                  <QuestionMarkCircleIcon className="-top-0.5 inline-block h-5 w-5 fill-current text-gray-400" />
                                }
                                content={feature.tooltip}
                              />
                            ) : feature.comingSoon ? (
                              <Text
                                fontSize={'xs'}
                                px={1}
                                color="primary.500"
                                bgColor="primary.25"
                                borderRadius="0.25rem"
                                whiteSpace={'nowrap'}
                              >
                                {feature.comingSoon}
                              </Text>
                            ) : (
                              <></>
                            )}
                          </Flex>
                        </Td>
                        {React.Children.toArray(
                          feature.tiers.map(tier => {
                            const lastBusinessCell =
                              tier.title === 'Business' &&
                              index === featureSections.length - 1 &&
                              featureIndex ===
                                featureSection.features.length - 1;

                            return (
                              <Td
                                key={`${feature.name} + ${tier.title}`}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                borderBottom={
                                  !lastBusinessCell
                                    ? '1px solid #E4E9F2'
                                    : 'none'
                                }
                                textAlign="center"
                                w="100%"
                              >
                                <Box minW="129px">
                                  {typeof tier.value === 'string' ? (
                                    <Text> {tier.value}</Text>
                                  ) : tier.value === true ? (
                                    <Icon
                                      as={CheckIcon}
                                      color={'primary.500'}
                                    />
                                  ) : (
                                    <Text>—</Text>
                                  )}
                                </Box>
                              </Td>
                            );
                          })
                        )}
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            ))
          )}
        </Table>
      </>
    </LayoutContainer>
  );
}
